import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Check as CheckIcon, Clear as ClearIcon, PriceChange } from '@mui/icons-material';
import { Box, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { DateTimePicker } from '@mui/x-date-pickers';

import useNotification from '../../../../core/hooks/use_notification';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_DATE_ONLY_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../core/constants';
import { Button, DeleteButton, SubmitButton } from '../../../common/button';
import { deletePromotionNotificationData, getPromotionNotificationData, updatePromotionNotificationData } from '../../../../core/utils/notification_utils';
import { getProductExpirationPromotionAction } from '../../../../features/price/get_product_expiration_promotion';
import { createOrUpdateProductExpirationPromotionsAction } from '../../../../features/price/create_or_update_product_expiration_promotions';
import { deleteProductExpirationPromotionAction } from '../../../../features/price/delete_product_expiration_promotion';
import { updateProductExpirationPromotionAction } from '../../../../features/price/update_product_expiration_promotion';

export default function ExpirationPromotionDialog({ resource, podId, refreshList }) {
    const [open, setOpen] = useState(false);
    const [discount, setDiscount] = useState('');
    const [currentGrossPrice, setCurrentGrossPrice] = useState('');
    const [timeFrom, setTimeFrom] = useState(null);
    const [expirationPromotionId, setExpirationPromotionId] = useState(false);

    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const calculateDiscount = (base, percentage) => (parseFloat(base).toFixed(2) - parseFloat((percentage / 100 * base).toFixed(2))).toFixed(2);

    const loadPromotion = () => {
        dispatch(getProductExpirationPromotionAction({
            productId: resource?.productId,
            productBatchId: resource?.productBatchId,
            podId
        }))
            .then(response => {
                showErrorMessage(getPromotionNotificationData(response));
                reloadValues(response?.payload);
            });
    };

    const reloadValues = (initialValue) => {
        setDiscount(initialValue?.discount ? parseFloat(initialValue?.discount).toFixed(2) : '');
        setCurrentGrossPrice(initialValue?.currentProductGrossPrice ? parseFloat(initialValue?.currentProductGrossPrice).toFixed(2) : '');
        setTimeFrom(initialValue?.timeFrom ? moment(initialValue?.timeFrom) : null);
        setExpirationPromotionId(initialValue?.expirationPromotionId);
    };

    const onOpen = () => {
        loadPromotion();
        setOpen(true);
    };

    const onSubmit = () => {
        if (discount && timeFrom?._isValid) {
            if (expirationPromotionId) {
                const form = {
                    timeFrom: moment(timeFrom).format(MISC_DATE_ISO_FORMAT),
                    timeTo: moment(resource?.expiryDate + 'T23:59:59', MISC_DATE_ISO_FORMAT).format(MISC_DATE_ISO_FORMAT),
                    discount
                };

                dispatch(updateProductExpirationPromotionAction({ expirationPromotionId, form }))
                    .then(response => showNotification(updatePromotionNotificationData(response)));
            } else {
                const form = {
                    podIds: [podId],
                    productBatchIds: [resource?.productBatchId],
                    timeFrom: moment(timeFrom).format(MISC_DATE_ISO_FORMAT),
                    timeTo: moment(resource?.expiryDate + 'T23:59:59', MISC_DATE_ISO_FORMAT).format(MISC_DATE_ISO_FORMAT),
                    discount
                };

                dispatch(createOrUpdateProductExpirationPromotionsAction({ productId: resource?.productId, form }))
                    .then(response => showNotification(updatePromotionNotificationData(response)));
            }

            refreshList();
        }

        onClose();
    };

    const onDelete = () => {
        setDiscount('');
        setTimeFrom(null);

        if (expirationPromotionId) {
            dispatch(deleteProductExpirationPromotionAction(expirationPromotionId))
                .then(response => showNotification(deletePromotionNotificationData(response)));
            refreshList();
        }
    };

    const onClose = () => {
        setOpen(false);
    };

    return (
        <div>
            <PriceChange onClick={ onOpen } cursor="pointer"/>
            <Dialog open={ open } onClose={ onClose } fullWidth={ true } maxWidth={ 'lg' }>
                <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Promocja na krótki termin przydatności</DialogTitle>
                <Divider/>

                <DialogContent sx={ { py: 3 } }>
                    <Box sx={ {
                        p: 2,
                        backgroundColor: 'slate.50',
                        borderRadius: 2,
                        mb: 3,
                        border: theme => `1px solid ${ theme.palette.divider }`
                    } }>
                        <Typography variant="body1">
                            <b>{ resource?.productName }</b>, partia: { resource?.productBatchId } (nr
                            magazynowy: { resource?.warehouseBatchNumber })
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={ { mt: 0.5 } }>
                            Data
                            zatowarowania: <b>{ moment(resource?.stockingDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }</b> |
                            Data
                            przydatności: <b>{ moment(resource?.expiryDate, MISC_DATE_ISO_DATE_ONLY_FORMAT).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) }</b>
                        </Typography>
                    </Box>

                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell align="center">Aktualna liczba produktów</TableCell>
                                    <TableCell align="center" sx={ { borderLeft: theme => `1px solid ${ theme.palette.divider }` } }>Zablokowana
                                        przez prom. na produkt</TableCell>
                                    <TableCell align="center">Cena zakupu</TableCell>
                                    <TableCell align="center">Cena bazowa</TableCell>
                                    <TableCell align="center">Aktualna cena produktu</TableCell>
                                    <TableCell align="center" sx={ { minWidth: 120, maxWidth: 120 } }>Upust
                                        [%]</TableCell>
                                    <TableCell align="center">Cena promocyjna końcowa</TableCell>
                                    <TableCell align="center">Promocja od</TableCell>
                                    <TableCell align="center"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        align="center">{ resource?.numberOfStockedItems - resource?.numberOfSoldItems }</TableCell>
                                    <TableCell align="center" sx={ { borderLeft: theme => `1px solid ${ theme.palette.divider }` } }>
                                        {
                                            resource?.excludeExpirationPromotion
                                                ? <CheckIcon color="success"/>
                                                : <ClearIcon color="error"/>
                                        }
                                    </TableCell>
                                    <TableCell align="center">{ resource?.purchaseGrossPrice?.toFixed(2) }</TableCell>
                                    <TableCell align="center">{ resource?.basicGrossPrice?.toFixed(2) }</TableCell>
                                    <TableCell align="center"
                                               sx={ { fontWeight: 600 } }>{ currentGrossPrice }</TableCell>
                                    <TableCell align="center">
                                        <TextField
                                            size="small"
                                            value={ discount }
                                            type="number"
                                            inputProps={ { min: '0.01', max: '100.00', step: '0.01' } }
                                            onChange={ event => setDiscount(event.target.value) }
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={ { fontWeight: 600 } }>
                                        {
                                            resource?.excludeExpirationPromotion
                                                ? currentGrossPrice
                                                : discount ? calculateDiscount(currentGrossPrice, discount) : ''
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <DateTimePicker
                                            value={ timeFrom }
                                            onChange={ newDate => setTimeFrom(newDate) }
                                            sx={ { width: 220 } }
                                            slotProps={ {
                                                textField: {
                                                    size: 'small',
                                                    InputLabelProps: { shrink: true }
                                                }
                                            } }
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {
                                            expirationPromotionId &&
                                            <DeleteButton onDeleteClick={ () => onDelete() }/>
                                        }
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

                <Divider/>
                <DialogActions sx={ { p: 2, px: 3, gap: 1 } }>
                    <Button variant="outlined" onClick={ onClose }>Anuluj</Button>
                    <SubmitButton onClick={ onSubmit } disabled={ !discount || !timeFrom }>Zapisz</SubmitButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}